import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Student } from './student';
export { Student };
import { studentSchema } from '@schemas/business-objects/student';
import { AlternateStudentFormArray } from './alternate-student-form-group';
import { AttendanceFormArray } from './attendance-form-group';
import { OrganizationFormGroup } from './organization-form-group';
import { PrescriptionFormArray } from './prescription-form-group';
import { SelectedStudentFormArray } from './selected-student-form-group';
import { SessionHistoryFormArray } from './session-history-form-group';
import { TestResultFormArray } from './test-result-form-group';
import { UserFormGroup } from './user-form-group';
import type { Curriculum } from './curriculum';
import type { NotAvailable } from './not-available';
import type { Resource } from './resource';
import type { Role } from './role';
import type { RoleAssignment } from './role-assignment';
import type { Session } from './session';
import type { SessionActivityItem } from './session-activity-item';
import type { Slot } from './slot';
import type { StudentAssignment } from './student-assignment';
import type { Term } from './term';
import type { TermTime } from './term-time';

export class StudentFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    firstName: SchemaFormControl;
    lastName: SchemaFormControl;
    email: SchemaFormControl;
    dateOfBirth: SchemaFormControl;
    currentGradeLevel: SchemaFormControl;
    created: SchemaFormControl;
    organizationId: SchemaFormControl;
    organization: OrganizationFormGroup;
    appUserId: SchemaFormControl;
    appUser: UserFormGroup;
    prescriptions: PrescriptionFormArray;
    testResults: TestResultFormArray;
    selectedStudents: SelectedStudentFormArray;
    alternateStudents: AlternateStudentFormArray;
    attendances: AttendanceFormArray;
    sessionHistories: SessionHistoryFormArray;
  };

  declare value: Partial<Student>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(studentSchema, Student, data, options);
  }

  get object(): Student {
    return new Student(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'organization':
        return new OrganizationFormGroup();
      case 'appUser':
        return new UserFormGroup();
      case 'prescriptions':
        return new PrescriptionFormArray();
      case 'testResults':
        return new TestResultFormArray();
      case 'selectedStudents':
        return new SelectedStudentFormArray();
      case 'alternateStudents':
        return new AlternateStudentFormArray();
      case 'attendances':
        return new AttendanceFormArray();
      case 'sessionHistories':
        return new SessionHistoryFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Student[]> {
    return Student.find(filter);
  }

  static async findOne(filter?: any): Promise<Student | null> {
    return Student.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<Student> {
    return Student.findById(id, filter);
  }

  static async create(data: Partial<Student>): Promise<Student> {
    return Student.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<Student>,
  ): Promise<Student> {
    return Student.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return Student.deleteById(id);
  }

  async getLinkedResources(): Promise<any[]> {
    const instance = new Student(this.getRawValue());
    return instance.getLinkedResources();
  }

  static async forCurrentUser(): Promise<any> {
    return Student.forCurrentUser();
  }

  async findProposedAssignments(effectiveFrom: string): Promise<any[]> {
    const instance = new Student(this.getRawValue());
    return instance.findProposedAssignments(effectiveFrom);
  }

  async effectOutAssignmentsOnOrganizationChange(
    oldOrganizationId: number,
    effectiveFrom: string,
  ): Promise<any> {
    const instance = new Student(this.getRawValue());
    return instance.effectOutAssignmentsOnOrganizationChange(
      oldOrganizationId,
      effectiveFrom,
    );
  }
}

export class StudentFormArray extends PersistedFormArray {
  readonly entityName = 'Student' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(studentSchema, Student, options);
  }

  protected override createItemGroup() {
    return new StudentFormGroup();
  }

  get array(): Student[] {
    return this.controls.map((group: StudentFormGroup) => group.object);
  }
}

export class StudentPersistedArray extends PersistedArray<Student> {
  readonly entityName = 'Student' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Student, options);
  }
}
